/**
 * DailyLogCalendar Component
 * Calendar view for daily logs
 * BuilderBel Construction Project Management System
 */

import { AlertTriangle, Calendar, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import { useGetDailyLogCalendarQuery } from '../../services/dailyLogApi';
import type { CalendarData, DailyLogStatus } from '../../types/dailyLog';

interface DailyLogCalendarProps {
  onSelectDate: (date: string, logId?: string) => void;
  onCreateLog: (date: string) => void;
}

// Day names
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Month names
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// Status colors for calendar
const STATUS_COLORS: Record<DailyLogStatus, string> = {
  draft: 'bg-gray-200 border-gray-300',
  submitted: 'bg-blue-200 border-blue-300',
  approved: 'bg-green-200 border-green-300',
  rejected: 'bg-red-200 border-red-300',
};

const STATUS_DOT_COLORS: Record<DailyLogStatus, string> = {
  draft: 'bg-gray-500',
  submitted: 'bg-blue-500',
  approved: 'bg-green-500',
  rejected: 'bg-red-500',
};

export const DailyLogCalendar: React.FC<DailyLogCalendarProps> = ({
  onSelectDate,
  onCreateLog,
}) => {
  const projectId = useCurrentProjectId();
  const today = new Date();

  // State for current month view
  const [currentDate, setCurrentDate] = useState(new Date());

  // Get calendar data
  const { data: response, isLoading } = useGetDailyLogCalendarQuery(
    {
      projectId: projectId!,
      params: {
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
      },
    },
    { skip: !projectId }
  );

  const calendarData = response?.data || [];

  // Create a map of date -> log data for quick lookup
  const logsByDate = useMemo(() => {
    const map = new Map<string, CalendarData>();
    calendarData.forEach(item => {
      // Convert date to YYYY-MM-DD format
      const dateKey = item.date.split('T')[0];
      map.set(dateKey, item);
    });
    return map;
  }, [calendarData]);

  // Calculate calendar grid
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // First day of month
    const firstDay = new Date(year, month, 1);
    // Last day of month
    const lastDay = new Date(year, month + 1, 0);

    // Start from the Sunday of the week containing the first day
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    // End on the Saturday of the week containing the last day
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    const days: Array<{
      date: Date;
      isCurrentMonth: boolean;
      isToday: boolean;
      log?: CalendarData;
    }> = [];

    const current = new Date(startDate);
    while (current <= endDate) {
      const dateKey = current.toISOString().split('T')[0];
      days.push({
        date: new Date(current),
        isCurrentMonth: current.getMonth() === month,
        isToday:
          current.getDate() === today.getDate() &&
          current.getMonth() === today.getMonth() &&
          current.getFullYear() === today.getFullYear(),
        log: logsByDate.get(dateKey),
      });
      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [currentDate, logsByDate, today]);

  // Navigation handlers
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Handle day click
  const handleDayClick = (day: (typeof calendarDays)[0]) => {
    const dateStr = day.date.toISOString().split('T')[0];
    if (day.log) {
      onSelectDate(dateStr, day.log.logId);
    } else if (day.date <= today) {
      // Can only create logs for today or past dates
      onCreateLog(dateStr);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <button
            type="button"
            onClick={goToToday}
            className="px-3 py-1.5 text-sm text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
          >
            Today
          </button>
        </div>

        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={goToPreviousMonth}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={goToNextMonth}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center space-x-6 px-6 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${STATUS_DOT_COLORS.draft}`} />
          <span className="text-xs text-gray-600">Draft</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${STATUS_DOT_COLORS.submitted}`} />
          <span className="text-xs text-gray-600">Submitted</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${STATUS_DOT_COLORS.approved}`} />
          <span className="text-xs text-gray-600">Approved</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${STATUS_DOT_COLORS.rejected}`} />
          <span className="text-xs text-gray-600">Rejected</span>
        </div>
        <div className="flex items-center space-x-2">
          <AlertTriangle className="w-3 h-3 text-yellow-500" />
          <span className="text-xs text-gray-600">Has Delays</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 p-4 overflow-auto">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
          </div>
        ) : (
          <div className="h-full">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DAY_NAMES.map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1 h-[calc(100%-32px)]">
              {calendarDays.map((day, index) => {
                const isFuture = day.date > today;
                const canCreate = !day.log && day.isCurrentMonth && !isFuture;

                return (
                  <div
                    key={index}
                    onClick={() => !isFuture && handleDayClick(day)}
                    className={`
                      relative min-h-[80px] p-2 rounded-lg border transition-all
                      ${day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                      ${day.isToday ? 'ring-2 ring-brand-500 border-brand-500' : 'border-gray-200'}
                      ${isFuture ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-brand-300'}
                      ${day.log ? STATUS_COLORS[day.log.status] : ''}
                    `}
                  >
                    {/* Date Number */}
                    <div
                      className={`
                        text-sm font-medium
                        ${day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                        ${day.isToday ? 'text-brand-600' : ''}
                      `}
                    >
                      {day.date.getDate()}
                    </div>

                    {/* Log Content */}
                    {day.log && (
                      <div className="mt-1 space-y-1">
                        {/* Log Number */}
                        <div className="text-xs font-medium text-gray-700">{day.log.logNumber}</div>

                        {/* Day Number */}
                        <div className="text-xs text-gray-500">Day {day.log.dayNumber}</div>

                        {/* Workers */}
                        <div className="text-xs text-gray-600">{day.log.totalWorkers} workers</div>

                        {/* Delay Indicator */}
                        {day.log.hasDelays && (
                          <div className="absolute top-2 right-2">
                            <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          </div>
                        )}

                        {/* Status Dot */}
                        <div className="absolute bottom-2 right-2">
                          <div
                            className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT_COLORS[day.log.status]}`}
                          />
                        </div>
                      </div>
                    )}

                    {/* Add Log Indicator */}
                    {canCreate && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-white/80 rounded-lg">
                        <div className="flex flex-col items-center text-brand-600">
                          <Plus className="h-5 w-5" />
                          <span className="text-xs mt-1">Add Log</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">{calendarData.length} logs this month</span>
          </div>
          <div className="text-gray-300">|</div>
          <div className="text-gray-600">
            {calendarData.filter(l => l.status === 'approved').length} approved
          </div>
          <div className="text-gray-300">|</div>
          <div className="text-gray-600">
            {calendarData.filter(l => l.status === 'submitted').length} pending
          </div>
          <div className="text-gray-300">|</div>
          <div className="text-gray-600">
            {calendarData.filter(l => l.hasDelays).length} with delays
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyLogCalendar;
